(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    formSelector: 'form[name="__upez_cart_checkout"]',
    popupSelector: '[data-popup]',
    overlaySelector: '[data-popup-overlay]',
    closeSelector: '[data-popup-close]',
    proceedCheckoutSelector: '[data-proceed-checkout]',
    productCardSelector: '[data-product-card]',
    addToCartSelector: '[data-add-to-cart]',
    productsContainerSelector: '[data-products-container]',
    emptyMessageSelector: '[data-empty-message]',
    bodyClass: 'cross-sell-popup-open',
    openClass: 'cross-sell-popup--open',
    hiddenClass: 'cross-sell-product-card--hidden',
    loadingClass: 'cross-sell-product-card__button--loading',
    sessionInitKey: 'cross_sell_session_init',
    hasVisitedKey: 'cross_sell_has_visited'
  };

  // State
  let state = {
    popup: null,
    form: null,
    lastFocusedElement: null,
    shouldBypassIntercept: false,
    cartItems: [],
    observer: null
  };

  /**
   * Initialize the cross-sell popup
   */
  function init() {
    state.popup = document.querySelector(CONFIG.popupSelector);
    
    if (!state.popup) {
      return;
    }

    // Initialize session tracking
    initializeSessionTracking();

    // Find and bind the checkout form
    bindCheckoutForm();

    // Setup mutation observer to handle dynamic form replacements
    setupMutationObserver();

    // Setup popup event listeners
    setupPopupListeners();

    // Setup add to cart listeners
    setupAddToCartListeners();

    // Setup keyboard navigation
    setupKeyboardNavigation();

    // Setup slider navigation (desktop only)
    setupSliderNavigation();

    // Setup pagination dots
    setupPaginationDots();
  }

  /**
   * Bind event listeners to the checkout form
   */
  function bindCheckoutForm() {
    state.form = document.querySelector(CONFIG.formSelector);
    
    if (!state.form) {
      return;
    }

    // Intercept form submission
    state.form.addEventListener('submit', handleCheckoutIntercept, true);

    // Intercept clicks on form and its children
    state.form.addEventListener('click', handleCheckoutClick, true);
  }

  /**   * Initialize session tracking to detect first-time vs returning visitors
   */
  function initializeSessionTracking() {
    try {
      // Check if this session has already been initialized
      const sessionInitialized = sessionStorage.getItem(CONFIG.sessionInitKey) === '1';
      
      if (!sessionInitialized) {
        // First page load in this session
        // Check if user has visited before (from localStorage set in a previous session)
        const hasVisitedBefore = localStorage.getItem(CONFIG.hasVisitedKey) === '1';
        
        // Mark this session as initialized
        sessionStorage.setItem(CONFIG.sessionInitKey, '1');
        
        // Mark that user has now visited (for future sessions)
        localStorage.setItem(CONFIG.hasVisitedKey, '1');
        
        // Store whether this was their first visit (for use by isReturningVisitor)
        // This value will persist throughout the entire session
        sessionStorage.setItem('is_returning', hasVisitedBefore ? '1' : '0');
      }
      // If session already initialized, keep the existing sessionStorage values
    } catch (error) {
      // sessionStorage/localStorage not available
      console.error('Storage not available:', error);
    }
  }

  /**
   * Check if user is a returning visitor (not their first session)
   */
  function isReturningVisitor() {
    try {
      // Check the flag we set during initialization
      // This tells us if localStorage had the visited flag BEFORE this session started
      return sessionStorage.getItem('is_returning') === '1';
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if popup should be bypassed based on settings
   */
  function shouldBypassPopup() {
    const desktopOnly = state.popup.getAttribute('data-desktop-only') === 'true';
    const isDesktop = window.innerWidth >= 769;
    const returningVisitorsOnly = state.popup.getAttribute('data-returning-visitors-only') === 'true';

    if (desktopOnly && !isDesktop) {
      return true;
    }

    if (returningVisitorsOnly && !isReturningVisitor()) {
      return true;
    }

    return false;
  }

  /**   * Setup MutationObserver to detect when form is replaced in DOM
   */
  function setupMutationObserver() {
    if (state.observer) {
      state.observer.disconnect();
    }

    state.observer = new MutationObserver(function(mutations) {
      // Check if we need to rebind the form
      const currentForm = document.querySelector(CONFIG.formSelector);
      
      if (currentForm && currentForm !== state.form) {
        bindCheckoutForm();
      }
    });

    // Observe the entire document body for changes
    state.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handle checkout form submission
   */
  function handleCheckoutIntercept(event) {
    // Check if we should bypass entirely (e.g., first-time visitors)
    if (shouldBypassPopup()) {
      return; // Don't intercept at all
    }

    if (state.shouldBypassIntercept) {
      // Allow the checkout to proceed
      state.shouldBypassIntercept = false;
      return;
    }

    // Prevent the default form submission
    event.preventDefault();
    event.stopPropagation();

    // Open the popup
    openPopup();
  }

  /**
   * Handle clicks on checkout form elements
   */
  function handleCheckoutClick(event) {
    // Check if we should bypass entirely (e.g., first-time visitors)
    if (shouldBypassPopup()) {
      return; // Don't intercept at all
    }

    // Check if the click is on a submit button or checkout trigger
    const target = event.target;
    const isCheckoutButton = target.matches('button[name="checkout"], button[type="submit"], input[type="submit"]') ||
                            target.closest('button[name="checkout"], button[type="submit"], input[type="submit"]');
    
    // Check if the click is on the direct child div of the checkout form
    const isFormChildDiv = target.matches('form[name="__upez_cart_checkout"] > div') ||
                          target.closest('form[name="__upez_cart_checkout"] > div');

    if ((isCheckoutButton || isFormChildDiv) && !state.shouldBypassIntercept) {
      event.preventDefault();
      event.stopPropagation();
      openPopup();
    }
  }

  /**
   * Setup popup event listeners
   */
  function setupPopupListeners() {
    // Close button
    const closeButtons = state.popup.querySelectorAll(CONFIG.closeSelector);
    closeButtons.forEach(function(button) {
      button.addEventListener('click', closePopup);
    });

    // Overlay click
    const overlay = state.popup.querySelector(CONFIG.overlaySelector);
    if (overlay) {
      overlay.addEventListener('click', closePopup);
    }

    // Proceed to checkout button
    const proceedButton = state.popup.querySelector(CONFIG.proceedCheckoutSelector);
    if (proceedButton) {
      proceedButton.addEventListener('click', proceedToCheckout);
    }
  }

  /**
   * Setup add to cart button listeners
   */
  function setupAddToCartListeners() {
    const addToCartButtons = state.popup.querySelectorAll(CONFIG.addToCartSelector);
    
    addToCartButtons.forEach(function(button) {
      button.addEventListener('click', handleAddToCart);
    });
  }

  /**
   * Setup keyboard navigation
   */
  function setupKeyboardNavigation() {
    document.addEventListener('keydown', function(event) {
      if (!state.popup.classList.contains(CONFIG.openClass)) {
        return;
      }

      // Close on ESC key
      if (event.key === 'Escape') {
        closePopup();
      }

      // Trap focus within modal
      if (event.key === 'Tab') {
        trapFocus(event);
      }
    });
  }

  /**
   * Setup pagination dots
   */
  function setupPaginationDots() {
    const paginationContainer = state.popup.querySelector('[data-pagination]');
    const slider = state.popup.querySelector('[data-slider]');

    if (!paginationContainer || !slider) {
      return;
    }

    // Check if desktop (769px+)
    function isDesktop() {
      return window.innerWidth >= 769;
    }

    // Create pagination dots based on visible products
    function createDots() {
      if (!isDesktop()) {
        paginationContainer.innerHTML = '';
        paginationContainer.style.display = 'none';
        return;
      }

      paginationContainer.style.display = 'flex';

      // Count visible products
      const visibleProducts = Array.from(slider.querySelectorAll('[data-product-card]')).filter(function(card) {
        return !card.classList.contains(CONFIG.hiddenClass);
      });

      const productsPerPage = 4;
      const totalPages = Math.ceil(visibleProducts.length / productsPerPage);

      // Clear existing dots
      paginationContainer.innerHTML = '';

      // Create dots
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.className = 'cross-sell-popup__pagination-dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.setAttribute('data-page', i);
        
        if (i === 0) {
          dot.classList.add('cross-sell-popup__pagination-dot--active');
        }

        dot.addEventListener('click', function() {
          const pageIndex = parseInt(this.getAttribute('data-page'), 10);
          const scrollAmount = slider.offsetWidth * pageIndex;
          slider.scrollTo({
            left: scrollAmount,
            behavior: 'smooth'
          });
        });

        paginationContainer.appendChild(dot);
      }
    }

    // Update active dot based on scroll position
    function updateActiveDot() {
      if (!isDesktop()) {
        return;
      }

      const dots = paginationContainer.querySelectorAll('.cross-sell-popup__pagination-dot');
      const scrollLeft = slider.scrollLeft;
      const containerWidth = slider.offsetWidth;
      const currentPage = Math.round(scrollLeft / containerWidth);

      dots.forEach(function(dot, index) {
        if (index === currentPage) {
          dot.classList.add('cross-sell-popup__pagination-dot--active');
        } else {
          dot.classList.remove('cross-sell-popup__pagination-dot--active');
        }
      });
    }

    // Initial creation
    createDots();

    // Update dots on scroll
    slider.addEventListener('scroll', updateActiveDot);

    // Recreate dots on window resize
    window.addEventListener('resize', function() {
      createDots();
      setTimeout(updateActiveDot, 100);
    });

    // Recreate dots when products are filtered (after cart update)
    const originalFilterFunction = window.filterProductsByCart;
    document.addEventListener('crossSell:opened', function() {
      setTimeout(function() {
        createDots();
        updateActiveDot();
      }, 200);
    });
  }

  /**
   * Setup slider navigation (desktop only)
   */
  function setupSliderNavigation() {
    const prevButton = state.popup.querySelector('[data-slider-prev]');
    const nextButton = state.popup.querySelector('[data-slider-next]');
    const slider = state.popup.querySelector('[data-slider]');

    if (!prevButton || !nextButton || !slider) {
      return;
    }

    // Check if desktop (769px+)
    function isDesktop() {
      return window.innerWidth >= 769;
    }

    // Previous button
    prevButton.addEventListener('click', function() {
      if (isDesktop()) {
        // Scroll by 4 products on desktop
        const containerWidth = slider.offsetWidth;
        slider.scrollBy({
          left: -containerWidth,
          behavior: 'smooth'
        });
      } else {
        // Scroll by 1 product on mobile
        const cardWidth = slider.querySelector('[data-product-card]')?.offsetWidth || 150;
        const gap = 12;
        slider.scrollBy({
          left: -(cardWidth + gap),
          behavior: 'smooth'
        });
      }
    });

    // Next button
    nextButton.addEventListener('click', function() {
      if (isDesktop()) {
        // Scroll by 4 products on desktop
        const containerWidth = slider.offsetWidth;
        slider.scrollBy({
          left: containerWidth,
          behavior: 'smooth'
        });
      } else {
        // Scroll by 1 product on mobile
        const cardWidth = slider.querySelector('[data-product-card]')?.offsetWidth || 150;
        const gap = 12;
        slider.scrollBy({
          left: cardWidth + gap,
          behavior: 'smooth'
        });
      }
    });

    // Update button states on scroll
    function updateButtonStates() {
      const isAtStart = slider.scrollLeft <= 1;
      const isAtEnd = Math.ceil(slider.scrollLeft + slider.clientWidth) >= slider.scrollWidth - 1;

      prevButton.disabled = isAtStart;
      nextButton.disabled = isAtEnd;
    }

    slider.addEventListener('scroll', updateButtonStates);
    
    // Update immediately and after a short delay to catch initial state
    setTimeout(updateButtonStates, 100);
    updateButtonStates(); // Initial state
    
    // Update on window resize
    window.addEventListener('resize', updateButtonStates);
  }

  /**
   * Open the popup
   */
  function openPopup() {
    // Store the last focused element
    state.lastFocusedElement = document.activeElement;

    // Fetch cart and filter products
    fetchCart().then(function() {
      filterProductsByCart();
      
      // Show the popup
      state.popup.classList.add(CONFIG.openClass);
      document.body.classList.add(CONFIG.bodyClass);

      // Focus the first focusable element
      focusFirstElement();

      // Update slider button states after popup is shown
      setTimeout(function() {
        const slider = state.popup.querySelector('[data-slider]');
        if (slider) {
          slider.dispatchEvent(new Event('scroll'));
        }
      }, 150);

      // Dispatch custom event
      dispatchEvent('crossSell:opened');

      // Update pagination dots after filtering
      setTimeout(function() {
        const paginationContainer = state.popup.querySelector('[data-pagination]');
        if (paginationContainer) {
          const event = new Event('updateDots');
          paginationContainer.dispatchEvent(event);
        }
      }, 100);
    });
  }

  /**
   * Close the popup
   */
  function closePopup() {
    state.popup.classList.remove(CONFIG.openClass);
    document.body.classList.remove(CONFIG.bodyClass);

    // Restore focus
    if (state.lastFocusedElement) {
      state.lastFocusedElement.focus();
    }

    // Dispatch custom event
    dispatchEvent('crossSell:closed');
  }

  /**
   * Proceed to checkout
   */
  function proceedToCheckout() {
    // Set bypass flag to prevent infinite loop
    state.shouldBypassIntercept = true;

    // Close the popup
    closePopup();

    // Click the original checkout form button
    setTimeout(function() {
      if (state.form) {
        const submitButton = state.form.querySelector('button[name="checkout"], button[type="submit"], input[type="submit"]');
        if (submitButton) {
          submitButton.click();
        } else {
          // Fallback: submit the form directly
          state.form.submit();
        }
      } else {
        // Fallback: navigate to checkout
        window.location.href = '/checkout';
      }
    }, 100);
  }

  /**
   * Fetch current cart items
   */
  function fetchCart() {
    return fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        state.cartItems = cart.items || [];
        return cart;
      })
      .catch(function(error) {
        console.error('Error fetching cart:', error);
        state.cartItems = [];
      });
  }

  /**
   * Filter products by checking if they're already in cart
   * Show only first 10 products that aren't in cart (random products fill the gaps)
   */
  function filterProductsByCart() {
    const productCards = state.popup.querySelectorAll(CONFIG.productCardSelector);
    const productsContainer = state.popup.querySelector(CONFIG.productsContainerSelector);
    const emptyMessage = state.popup.querySelector(CONFIG.emptyMessageSelector);
    
    let visibleCount = 0;
    const maxVisible = 10;

    // Get product IDs from cart
    const cartProductIds = state.cartItems.map(function(item) {
      return item.product_id;
    });

    // First pass: hide all products that are in cart
    productCards.forEach(function(card) {
      const productId = parseInt(card.getAttribute('data-product-id'), 10);
      
      if (cartProductIds.includes(productId)) {
        card.classList.add(CONFIG.hiddenClass);
      } else {
        card.classList.remove(CONFIG.hiddenClass);
      }
    });

    // Second pass: show only first 10 products that aren't in cart
    productCards.forEach(function(card) {
      const isInCart = cartProductIds.includes(parseInt(card.getAttribute('data-product-id'), 10));
      
      if (!isInCart && visibleCount < maxVisible) {
        card.classList.remove(CONFIG.hiddenClass);
        visibleCount++;
      } else if (!isInCart && visibleCount >= maxVisible) {
        // Hide extra products beyond the first 10 visible
        card.classList.add(CONFIG.hiddenClass);
      }
    });

    // Show empty message if no products are visible
    if (emptyMessage) {
      if (visibleCount === 0) {
        emptyMessage.style.display = 'block';
      } else {
        emptyMessage.style.display = 'none';
      }
    }
  }

  /**
   * Handle add to cart button click
   */
  function handleAddToCart(event) {
    const button = event.currentTarget;
    const variantId = button.getAttribute('data-variant-id');
    const productCard = button.closest(CONFIG.productCardSelector);

    if (!variantId || button.disabled) {
      return;
    }

    // Set loading state
    button.classList.add(CONFIG.loadingClass);
    button.disabled = true;

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      return response.json();
    })
    .then(function(item) {
      // Success - refresh cart and update UI
      return fetchCart().then(function() {
        filterProductsByCart();
        
        // Dispatch custom event
        dispatchEvent('crossSell:added', { item: item });

        // Remove loading state
        button.classList.remove(CONFIG.loadingClass);
        button.disabled = false;
      });
    })
    .catch(function(error) {
      console.error('Error adding to cart:', error);
      
      // Remove loading state
      button.classList.remove(CONFIG.loadingClass);
      button.disabled = false;
      
      // Show error (could be enhanced with a toast notification)
      alert('Could not add product to cart. Please try again.');
    });
  }

  /**
   * Focus the first focusable element in the modal
   */
  function focusFirstElement() {
    const focusableElements = state.popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * Trap focus within the modal
   */
  function trapFocus(event) {
    const focusableElements = state.popup.querySelectorAll(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Dispatch custom event
   */
  function dispatchEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail: detail || {},
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize if needed after page transitions (for SPA-like behavior)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail && event.detail.sectionId && event.detail.sectionId.includes('checkout-cross-sell')) {
      setTimeout(init, 100);
    }
  });

})();
